import { useRef, useState } from 'react'
import FigmaSlopCalendar from '@/components/figma-slop-calendar'
import FigmaSlopList from '@/components/figma-slop-calendar/list'
import FigmaSlopContent from '@/components/figma-slop-calendar/content'
import FigmaSlopChat from '@/components/figma-slop-chat'
import { Upload, File, X, Loader } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import { cn, uploadToS3 } from '@/lib/utils'
import { captureError } from '@/lib/errors'
import { toast } from 'sonner'
import { type RouterOutput, trpc } from '@/config/trpc'
import { DriveFilePicker } from '@/components/ui/drive-file-picker'
import type {
  CreatePostScheduleMessage,
  CreatePostScheduleTools,
} from '@backend/routes/stream/createPostSchedule'
import { type FileWithUrl } from '@backend/lib/db/typeRoots/fileWithUrl'

export type Campaign = RouterOutput['campaign']['get']

type Props = { campaign: Campaign }

export default function ScheduleAgentPage({ campaign }: Props) {
  const [isFileThingyOpen, setIsFileThingyOpen] = useState(campaign.index.length === 0)
  const [files, setFiles] = useState<FileWithUrl[]>(campaign.index.map(({ file }) => file))
  const [view, setView] = useState<'calendar' | 'list' | 'details'>('calendar')

  const [inputValue, setInputValue] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [fileAnalysisState, setFileAnalysisState] = useState<{
    uploading: string[]
    analyzing: string[]
  }>()

  // trpc

  const utils = trpc.useUtils()

  const { mutateAsync: generatePost } = trpc.scheduleAgent.generateCampaignPost.useMutation()
  const { mutateAsync: createFileIndex } = trpc.scheduleAgent.createFileIndex.useMutation()

  const { mutateAsync: updateCampaign } = trpc.campaign.update.useMutation()
  const { mutateAsync: createPosts } = trpc.campaign.post.create.useMutation()
  const { mutateAsync: updatePosts } = trpc.campaign.post.update.useMutation()
  const { mutateAsync: deleteAllPosts } = trpc.campaign.post.deleteAll.useMutation()

  const { mutateAsync: getUploadUrl } = trpc.asset.getUploadUrl.useMutation()
  const { mutateAsync: deleteAsset } = trpc.asset.delete.useMutation()

  const handleAddFiles = async (newFiles: FileWithUrl[]) => {
    const prevFiles = [...files]
    setFiles([...files, ...newFiles])

    const keys: string[] = []
    try {
      const uploadedFiles = newFiles.filter((f) => f.source === 'remote')
      const localFiles = newFiles.filter((f) => f.source === 'local')

      setFileAnalysisState({
        uploading: localFiles.map((f) => f.file.name),
        analyzing: [],
      })
      await Promise.all(
        localFiles.map(async (f) => {
          const { key, uploadUrl, assetUrl } = await getUploadUrl({
            type: 'campaign',
            campaignId: campaign.id,
          })
          keys.push(key)

          await uploadToS3(uploadUrl, f.file)
          uploadedFiles.push({
            source: 'remote' as const,
            url: assetUrl,
            key,
            file: { name: f.file.name, type: f.file.type, size: f.file.size },
          })
        }),
      )

      setFiles([...prevFiles, ...uploadedFiles])
      setFileAnalysisState({
        uploading: [],
        analyzing: uploadedFiles.map((f) => f.file.name),
      })

      const index = await createFileIndex({ files: uploadedFiles })

      // TODO: write file-index to db directly in createFileIndex?
      await updateCampaign({ id: campaign.id, index: [...campaign.index, ...index] })
      utils.campaign.get.invalidate({ id: campaign.id })
    } catch (e) {
      captureError(e, {
        flow: 'schedule-agent.addFiles',
        campaignId: campaign.id,
        fileCount: newFiles.length,
        totalBytes: newFiles.reduce((sum, f) => sum + f.file.size, 0),
      })
      await deleteAsset({ keys })
    } finally {
      setFileAnalysisState(undefined)
    }
  }

  const handleRemoveFile = async (file: FileWithUrl) => {
    setFiles(files.filter((f) => f.file.name !== file.file.name))

    await updateCampaign({
      id: campaign.id,
      index: campaign.index.filter(({ file: f }) => f.file.name !== file.file.name),
    })
    utils.campaign.get.invalidate({ id: campaign.id })

    if (file.source === 'remote') {
      await deleteAsset({ keys: [file.key] })
    }
  }

  const handleAnswerTool = async (answer: CreatePostScheduleTools['answer']['output']) => {
    console.log('answer', answer)

    await Promise.all([
      updateCampaign({
        id: campaign.id,
        title: answer.title,
        goal: answer.goal,
        description: answer.description,
        story: answer.story,
      }),
      deleteAllPosts({ campaignId: campaign.id }),
    ])

    await createPosts({
      campaignId: campaign.id,
      posts: answer.posts.map((p) => ({
        ...p,
        files: files.filter((f) => p.files.includes(f.file.name)) as Extract<
          FileWithUrl,
          { source: 'remote' }
        >[],
        scheduledAt: new Date(p.scheduledAt),
        caption: null,
      })),
    })

    utils.campaign.get.invalidate({ id: campaign.id })
  }

  const handleMessagesFinish = async (chatHistory: CreatePostScheduleMessage[]) => {
    await updateCampaign({
      id: campaign.id,
      chatHistory,
    })
    utils.campaign.get.invalidate({ id: campaign.id })
  }

  const generatePostCaptions = async () => {
    if (campaign.posts.length === 0) return

    setIsGenerating(true)

    try {
      const captions = await Promise.all(
        campaign.posts.map(async (post) => {
          const { caption } = await generatePost({
            topic: post.topic,
            campaignId: campaign.id,
          })
          return { postId: post.id, caption }
        }),
      )

      await updatePosts({
        campaignId: campaign.id,
        posts: captions.map(({ postId, caption }) => ({ id: postId, caption })),
      })

      utils.campaign.get.invalidate({ id: campaign.id })
    } catch (e) {
      captureError(e, {
        flow: 'schedule-agent.generateCaptions',
        campaignId: campaign.id,
        postCount: campaign.posts.length,
      })
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className="h-screen p-2">
      <div className="flex h-full gap-4">
        <div className="h-[calc(100vh-24px)] flex-1 overflow-y-scroll rounded-lg bg-white p-2">
          {view === 'calendar' ? (
            <FigmaSlopCalendar posts={campaign.posts} />
          ) : view === 'list' ? (
            <FigmaSlopList events={campaign.posts} files={files} />
          ) : (
            <FigmaSlopContent items={campaign.posts} />
          )}
        </div>

        <div className="flex max-w-[450px] flex-1 flex-col gap-2">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex gap-2">
              <Button
                variant={view === 'calendar' ? 'primary' : 'secondary'}
                onClick={() => setView('calendar')}>
                Calendar
              </Button>
              <Button
                variant={view === 'list' ? 'primary' : 'secondary'}
                onClick={() => setView('list')}>
                List
              </Button>
              <Button
                variant={view === 'details' ? 'primary' : 'secondary'}
                onClick={() => setView('details')}>
                Details
              </Button>
            </div>
            <Button
              onClick={generatePostCaptions}
              disabled={
                isGenerating ||
                !campaign ||
                campaign.posts.length === 0 ||
                campaign.posts.every((p) => p.caption !== null)
              }>
              {isGenerating ? 'Generating...' : 'Generate Captions'}
            </Button>
          </div>
          <div
            className={cn(
              'flex-2 flex overflow-y-auto rounded-lg bg-[#1f1f1f] p-4 transition-all duration-300',
              isFileThingyOpen ? 'flex-1' : 'flex-2',
            )}>
            <FileThingy
              isOpen={isFileThingyOpen}
              onToggle={() => setIsFileThingyOpen(!isFileThingyOpen)}
              files={files}
              fileAnalysisState={fileAnalysisState}
              setFileAnalysisState={setFileAnalysisState}
              onAddFiles={handleAddFiles}
              onRemoveFile={handleRemoveFile}
            />
          </div>
          <div
            className={cn(
              'overflow-hidden rounded-lg transition-all duration-300',
              isFileThingyOpen ? 'flex-2' : 'flex-1',
            )}>
            <FigmaSlopChat
              inputValue={inputValue}
              setInputValue={setInputValue}
              index={campaign.index}
              chatHistory={campaign.chatHistory}
              onAnswerTool={handleAnswerTool}
              onFinish={handleMessagesFinish}
              disabled={fileAnalysisState !== undefined}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const FileThingy = ({
  isOpen,
  onToggle,
  files,
  fileAnalysisState,
  setFileAnalysisState,
  onAddFiles,
  onRemoveFile,
}: {
  isOpen: boolean
  onToggle: () => void
  files: FileWithUrl[]
  fileAnalysisState?: { uploading: string[]; analyzing: string[] }
  setFileAnalysisState: (state: { uploading: string[]; analyzing: string[] }) => void
  onAddFiles: (files: FileWithUrl[]) => void
  onRemoveFile: (file: FileWithUrl) => void
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const { mutateAsync: deleteAssets } = trpc.asset.delete.useMutation()

  const handleFileUpload = (uploadedFiles: FileList | null) => {
    if (!uploadedFiles) return

    const incomingFiles = Array.from(uploadedFiles)
    const { valid } = verifyFiles(incomingFiles, files)

    onAddFiles(
      valid.map((f) => ({
        source: 'local' as const,
        url: URL.createObjectURL(f),
        file: f,
      })),
    )
  }

  const handleDriveFileTransfer = async (
    incomingFiles: { url: string; name: string; type: string; size: number; key: string }[],
  ) => {
    const { valid, rejected } = verifyFiles(incomingFiles, files)

    onAddFiles(
      valid.map((f) => ({
        source: 'remote' as const,
        url: f.url,
        key: f.key,
        file: { name: f.name, type: f.type, size: f.size },
      })),
    )

    if (rejected.length > 0) {
      await deleteAssets({ keys: rejected.map((f) => f.key) })
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (fileAnalysisState !== undefined) return
    handleFileUpload(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  return (
    <div className="flex h-full w-full flex-col gap-2">
      {/* Upload Area */}
      {isOpen && (
        <div>
          <div
            className={`mb-4 rounded-lg border-2 border-dashed p-3 text-center transition-colors ${
              isDragOver ? 'border-[#6864ff] bg-[#6864ff]/10' : 'border-[#444] hover:border-[#555]'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}>
            <Upload className="mx-auto mb-2 text-white/60" size={24} />
            <p className="mb-2 text-sm text-white/60">Drag & drop files here or</p>
            <div className="flex items-center justify-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                accept={CLAUDE_COMPATIBLE_TYPES.join(',')}
                onChange={(e) => handleFileUpload(e.target.files)}
              />
              <Button
                disabled={fileAnalysisState !== undefined}
                size="sm"
                onClick={() => fileInputRef.current?.click()}>
                Browse Files
              </Button>
              <DriveFilePicker
                destination={{ type: 'temp' }}
                onTransferStart={() => {
                  setFileAnalysisState({
                    uploading: [],
                    analyzing: [],
                  })
                }}
                onTansferComplete={handleDriveFileTransfer}
                disabled={fileAnalysisState !== undefined}
              />
            </div>
          </div>
          <div className="flex justify-center pb-2"></div>
        </div>
      )}

      <Button onClick={onToggle}>{isOpen ? 'Done' : 'Add Context'}</Button>
      <h4 className="mb-2 text-sm font-medium text-white/80">Uploaded Files ({files.length})</h4>

      {/* Files List */}
      {isOpen && (
        <div className="flex-1 overflow-y-auto">
          {files.length > 0 ? (
            <div className="space-y-2">
              {files.map((f) => {
                const isUploading = fileAnalysisState?.uploading.includes(f.file.name)
                const isAnalyzing = fileAnalysisState?.analyzing.includes(f.file.name)
                return (
                  <div
                    key={f.file.name}
                    className="flex items-center gap-3 rounded-lg bg-[#282828] p-3">
                    <File size={18} className="shrink-0 text-white/60" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-white">{f.file.name}</p>
                      <p className="text-xs text-white/50">
                        {formatFileSize(f.file.size)} • {f.file.type || 'Unknown type'}
                      </p>
                      {isUploading && <p className="text-xs text-white/50">Uploading...</p>}
                      {isAnalyzing && <p className="text-xs text-white/50">Analyzing...</p>}
                    </div>
                    {isUploading || isAnalyzing ? (
                      <Loader size={14} className="animate-spin text-white/40" />
                    ) : (
                      <Button
                        variant="ghost"
                        className="text-white/40 transition-colors hover:text-white/80"
                        size="icon-sm"
                        onClick={() => onRemoveFile(f)}>
                        <X size={14} />
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-white/40">No files uploaded yet</p>
          )}
        </div>
      )}
    </div>
  )
}

const verifyFiles = <T extends { name: string; type: string; size: number }>(
  incomingFiles: T[],
  files: FileWithUrl[],
) => {
  const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024
  const MAX_FILES = 20

  // Split incoming files by size
  const sizeValid = incomingFiles.filter((file) => file.size <= MAX_FILE_SIZE_BYTES)
  const rejectedFiles: T[] = incomingFiles.filter((file) => file.size > MAX_FILE_SIZE_BYTES)

  // Further split by allowed mime types
  const mimeValid = sizeValid.filter((file) => isMimeTypeAllowed(file.type))
  const mimeRejected = sizeValid.filter((file) => !isMimeTypeAllowed(file.type))
  if (mimeRejected.length > 0) {
    const previewNames = mimeRejected.map((f) => f.name).slice(0, 3)
    const remainingCount = mimeRejected.length - previewNames.length
    toast.error(
      <div className="text-left">
        <div className="font-medium">Unsupported file type</div>
        <div className="mt-1 text-sm opacity-80">These were skipped:</div>
        <ul className="mt-1 list-disc pl-5 text-sm">
          {previewNames.map((name) => (
            <li key={name} className="truncate">
              {name}
            </li>
          ))}
          {remainingCount > 0 && <li>+{remainingCount} more</li>}
        </ul>
      </div>,
      { duration: 5500 },
    )
  }
  const validFiles = mimeValid

  if (rejectedFiles.length > 0) {
    const previewNames = rejectedFiles.map((f) => f.name).slice(0, 3)
    const remainingCount = rejectedFiles.length - previewNames.length
    toast.error(
      <div className="text-left">
        <div className="font-medium">Some files exceed 4.5 MB</div>
        <div className="mt-1 text-sm opacity-80">These were skipped:</div>
        <ul className="mt-1 list-disc pl-5 text-sm">
          {previewNames.map((name) => (
            <li key={name} className="truncate">
              {name}
            </li>
          ))}
          {remainingCount > 0 && <li>+{remainingCount} more</li>}
        </ul>
      </div>,
      { duration: 5500 },
    )
  }

  // Filter out duplicate filenames against existing and within this batch
  const existingNames = new Set(files.map((f) => f.file.name))
  const seenNames = new Set(existingNames)
  const uniqueValidFiles: T[] = []
  const duplicateNameFiles: T[] = []
  for (const file of validFiles) {
    if (seenNames.has(file.name)) {
      duplicateNameFiles.push(file)
    } else {
      uniqueValidFiles.push(file)
      seenNames.add(file.name)
    }
  }

  if (duplicateNameFiles.length > 0) {
    const previewNames = duplicateNameFiles.map((f) => f.name).slice(0, 3)
    const remainingCount = duplicateNameFiles.length - previewNames.length
    toast.error(
      <div className="text-left">
        <div className="font-medium">Duplicate filenames skipped</div>
        <div className="mt-1 text-sm opacity-80">Files with the same name already exist:</div>
        <ul className="mt-1 list-disc pl-5 text-sm">
          {previewNames.map((name) => (
            <li key={name} className="truncate">
              {name}
            </li>
          ))}
          {remainingCount > 0 && <li>+{remainingCount} more</li>}
        </ul>
      </div>,
      { duration: 5500 },
    )
  }

  if (uniqueValidFiles.length > 0) {
    const remainingSlots = Math.max(0, MAX_FILES - files.length)
    if (remainingSlots === 0) {
      toast.error(
        <div className="text-left">
          <div className="font-medium">Maximum files reached</div>
          <div className="mt-1 text-sm opacity-80">
            You can only upload up to {MAX_FILES} files.
          </div>
        </div>,
        { duration: 4500 },
      )
      return {
        valid: [],
        rejected: [...rejectedFiles, ...mimeRejected, ...duplicateNameFiles, ...uniqueValidFiles],
      }
    }

    const filesToAdd = uniqueValidFiles.slice(0, remainingSlots)
    const overflow = uniqueValidFiles.slice(remainingSlots)

    if (overflow.length > 0) {
      const previewNames = overflow.map((f) => f.name).slice(0, 3)
      const remainingCount = overflow.length - previewNames.length
      toast.error(
        <div className="text-left">
          <div className="font-medium">Too many files (max {MAX_FILES})</div>
          <div className="mt-1 text-sm opacity-80">Extra files were skipped:</div>
          <ul className="mt-1 list-disc pl-5 text-sm">
            {previewNames.map((name) => (
              <li key={name} className="truncate">
                {name}
              </li>
            ))}
            {remainingCount > 0 && <li>+{remainingCount} more</li>}
          </ul>
        </div>,
        { duration: 5500 },
      )
    }

    return {
      valid: filesToAdd,
      rejected: [...rejectedFiles, ...mimeRejected, ...duplicateNameFiles, ...overflow],
    }
  }

  return { valid: [], rejected: [...rejectedFiles, ...mimeRejected, ...duplicateNameFiles] }
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Allowed MIME types for attachments the model can consume
const CLAUDE_COMPATIBLE_TYPES = [
  'image/*',
  'application/pdf',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/html',
  'text/plain',
  'text/markdown',
]

const isMimeTypeAllowed = (mime: string) => {
  if (!mime) return false
  return CLAUDE_COMPATIBLE_TYPES.some((allowed) => {
    if (allowed.endsWith('/*')) {
      return mime.startsWith(allowed.slice(0, allowed.indexOf('/')) + '/')
    }
    return mime === allowed
  })
}
